import { useEffect, useRef, useState, type ReactNode } from 'react'
import { MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { BuildingLink, LayoutBuilding, LinkKind } from '@/api/facilityApi'
import { campusBoxes, clampBox, linkSegment, type MapBox } from '@/lib/siteLayout'

/** How each kind of connection is drawn: a solid band, a band with a deck line, or a dashed line. */
export const LINK_STYLE: Record<LinkKind, { label: string; color: string; width: number; dash?: string; deck?: boolean }> = {
  corridor: { label: 'Link corridor', color: '#b4bac1', width: 9 },
  bridge: { label: 'Bridge', color: '#7d858e', width: 9, deck: true },
  tunnel: { label: 'Tunnel', color: '#6b7178', width: 3, dash: '6 4' },
}

/** The read-only campus (Overall View) is drawn at this many pixels a grid cell, and scrolls. */
const CELL_PX = 14

/** A press on a building becomes a move once the pointer travels this far; less is a click. */
const DRAG_THRESHOLD_PX = 4

type Gesture =
  | { kind: 'press'; id: string; startX: number; startY: number; from: MapBox }
  | { kind: 'move' | 'resize'; id: string; startX: number; startY: number; from: MapBox; box: MapBox }
  | { kind: 'connect'; id: string; x: number; y: number }

/**
 * The drawn site plan (Cleaning Visual, z8nrz7f731): each building a box on
 * the campus grid, where it was placed by hand or else automatically, with
 * its connections (corridor, bridge, tunnel) drawn between them.
 *
 * Editable (the Layout), with no modes to switch between:
 * - click a building to pick it; drag it to move it;
 * - drag its corner to resize it;
 * - drag the dot on its edge onto another building to connect them;
 * - click a connection to pick it.
 * Otherwise (the Overall View) a click picks a building and nothing moves.
 *
 * `fit` (read-only) crops to the buildings and draws them at a fixed, readable
 * scale; a campus larger than its panel scrolls rather than shrinking.
 */
export function CampusMap({
  buildings,
  links = [],
  selectedId,
  onSelect,
  badges,
  editable = false,
  fit = false,
  onPlace,
  onConnect,
  selectedLinkId = null,
  onSelectLink,
  renderBuilding,
}: {
  buildings: LayoutBuilding[]
  links?: BuildingLink[]
  selectedId: string | null
  onSelect: (id: string) => void
  badges?: Record<string, number>
  editable?: boolean
  fit?: boolean
  onPlace?: (box: MapBox) => void
  onConnect?: (fromId: string, toId: string) => void
  selectedLinkId?: string | null
  onSelectLink?: (id: string | null) => void
  /** A building's contents; default: its name, and a pin when picked. */
  renderBuilding?: (b: LayoutBuilding, selected: boolean) => ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const scroller = useRef<HTMLDivElement>(null)
  const [gesture, setGesture] = useState<Gesture | null>(null)
  const placed = campusBoxes(buildings)
  const { cols, rows } = placed
  const scroll = fit && !editable
  const boxes = placed.boxes
  const byId = new Map(buildings.map((b) => [b.id, b]))
  const live = gesture && (gesture.kind === 'move' || gesture.kind === 'resize') ? gesture : null
  const boxOf = (id: string) => (live?.id === id ? live.box : boxes.find((b) => b.id === id))

  // The window onto the grid: all of it, or (fit) just the buildings and a cell around them.
  const view =
    fit && boxes.length > 0
      ? (() => {
          const x0 = Math.min(...boxes.map((b) => b.x)) - 1.5
          const y0 = Math.min(...boxes.map((b) => b.y)) - 1.5
          const x1 = Math.max(...boxes.map((b) => b.x + b.w)) + 1.5
          const y1 = Math.max(...boxes.map((b) => b.y + b.h)) + 1.5
          return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }
        })()
      : { x: 0, y: 0, w: cols, h: rows }
  const pct = (v: number, of: number) => `${(v / of) * 100}%`

  // A building picked elsewhere (the floor list, a link) is scrolled into the panel.
  const picked = scroll ? boxes.find((b) => b.id === selectedId) : undefined
  const pickedKey = picked ? `${picked.id}:${picked.x}:${picked.y}` : ''
  useEffect(() => {
    const el = scroller.current
    if (!el || !picked) return
    const left = (picked.x - view.x) * CELL_PX
    const top = (picked.y - view.y) * CELL_PX
    const right = left + picked.w * CELL_PX
    const bottom = top + picked.h * CELL_PX
    if (left < el.scrollLeft || right > el.scrollLeft + el.clientWidth) el.scrollLeft = Math.max(0, left - CELL_PX * 2)
    if (top < el.scrollTop || bottom > el.scrollTop + el.clientHeight) el.scrollTop = Math.max(0, top - CELL_PX * 2)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the pick (or its place) changes
  }, [pickedKey])

  const rect = () => ref.current?.getBoundingClientRect()
  /** A client point in grid cells. */
  const toGrid = (clientX: number, clientY: number) => {
    const r = rect()
    if (!r) return { x: 0, y: 0 }
    return { x: view.x + ((clientX - r.left) / r.width) * view.w, y: view.y + ((clientY - r.top) / r.height) * view.h }
  }
  const cellPx = () => (rect()?.width ?? view.w) / view.w

  const begin = (e: React.PointerEvent, id: string, kind: 'press' | 'resize' | 'connect') => {
    if (!editable) return
    const from = boxOf(id)
    if (!from) return
    e.stopPropagation()
    ref.current?.setPointerCapture(e.pointerId)
    if (kind === 'connect') {
      const p = toGrid(e.clientX, e.clientY)
      setGesture({ kind: 'connect', id, x: p.x, y: p.y })
    } else if (kind === 'resize') {
      setGesture({ kind: 'resize', id, startX: e.clientX, startY: e.clientY, from, box: from })
    } else {
      setGesture({ kind: 'press', id, startX: e.clientX, startY: e.clientY, from })
    }
  }
  const onMove = (e: React.PointerEvent) => {
    if (!gesture) return
    if (gesture.kind === 'connect') {
      const p = toGrid(e.clientX, e.clientY)
      return setGesture({ ...gesture, x: p.x, y: p.y })
    }
    const dxPx = e.clientX - gesture.startX
    const dyPx = e.clientY - gesture.startY
    if (gesture.kind === 'press') {
      if (Math.hypot(dxPx, dyPx) < DRAG_THRESHOLD_PX) return
      return setGesture({ ...gesture, kind: 'move', box: gesture.from })
    }
    const dx = dxPx / cellPx()
    const dy = dyPx / cellPx()
    const f = gesture.from
    const next = gesture.kind === 'move' ? { ...f, x: f.x + dx, y: f.y + dy } : { ...f, w: f.w + dx, h: f.h + dy }
    setGesture({ ...gesture, box: clampBox(next, rows) })
  }
  const onUp = (e: React.PointerEvent) => {
    const g = gesture
    setGesture(null)
    if (!g) return
    if (g.kind === 'press') return onSelect(g.id)
    if (g.kind === 'connect') {
      // Dropped on another building: connect to it.
      const p = toGrid(e.clientX, e.clientY)
      const target = boxes.find((b) => b.id !== g.id && p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h)
      if (target) onConnect?.(g.id, target.id)
      return
    }
    const { from, box } = g
    if (box.x !== from.x || box.y !== from.y || box.w !== from.w || box.h !== from.h) onPlace?.(box)
  }

  const connecting = gesture?.kind === 'connect' ? gesture : null
  const connectFrom = connecting ? boxOf(connecting.id) : undefined
  const hovered = connecting
    ? boxes.find((b) => b.id !== connecting.id && connecting.x >= b.x && connecting.x <= b.x + b.w && connecting.y >= b.y && connecting.y <= b.y + b.h)?.id
    : undefined

  const map = (
    <div
      ref={ref}
      className={cn('relative select-none rounded-md bg-[#eef0f2]', scroll ? 'shrink-0' : 'w-full overflow-hidden', editable && 'touch-none')}
      style={{
        ...(scroll ? { width: view.w * CELL_PX, height: view.h * CELL_PX } : { aspectRatio: `${view.w} / ${view.h}` }),
        // The grid shows while a building moves, so it visibly snaps to it.
        backgroundImage: live ? 'radial-gradient(circle, #c3c8ce 1px, transparent 1.2px)' : undefined,
        backgroundSize: live ? `${100 / view.w}% ${100 / view.h}%` : undefined,
      }}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={() => setGesture(null)}
      onPointerDown={() => editable && onSelectLink?.(null)}
    >
      {!fit && <div className='pointer-events-none absolute inset-[2%] rounded-sm border-2 border-dashed border-[#a9adb2]' aria-hidden />}

      {/* Connections under the buildings, and the one being drawn. */}
      <svg className='absolute inset-0 h-full w-full' viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`} preserveAspectRatio='none'>
        {links.map((l) => {
          const a = boxOf(l.fromId)
          const b = boxOf(l.toId)
          if (!a || !b) return null
          const s = linkSegment(a, b)
          const style = LINK_STYLE[l.kind] ?? LINK_STYLE.corridor
          const color = l.id === selectedLinkId ? '#1a73e8' : style.color
          return (
            <g key={l.id}>
              <line {...s} stroke={color} strokeWidth={style.width} strokeDasharray={style.dash} vectorEffect='non-scaling-stroke' />
              {style.deck && <line {...s} stroke='#eef0f2' strokeWidth={2} vectorEffect='non-scaling-stroke' />}
              {editable && onSelectLink && (
                <line
                  {...s}
                  stroke='transparent'
                  strokeWidth={18}
                  vectorEffect='non-scaling-stroke'
                  className='cursor-pointer'
                  style={{ pointerEvents: 'stroke' }}
                  data-link={l.id}
                  onPointerDown={(e) => {
                    e.stopPropagation()
                    onSelectLink(l.id)
                  }}
                >
                  <title>{`${style.label}${l.label ? `: ${l.label}` : ''}. Click to change or remove it`}</title>
                </line>
              )}
            </g>
          )
        })}
        {connecting && connectFrom && (
          <line
            x1={connectFrom.x + connectFrom.w}
            y1={connectFrom.y + connectFrom.h / 2}
            x2={connecting.x}
            y2={connecting.y}
            stroke='#1a73e8'
            strokeWidth={3}
            strokeDasharray='6 4'
            vectorEffect='non-scaling-stroke'
          />
        )}
      </svg>
      {links.map((l) => {
        const a = boxOf(l.fromId)
        const b = boxOf(l.toId)
        // Not in a cropped panel: too small to hold a label without covering the buildings.
        if (!a || !b || !l.label || fit) return null
        const s = linkSegment(a, b)
        return (
          <span
            key={`label-${l.id}`}
            className='pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded bg-white/85 px-1 text-[10px] text-gray-600'
            style={{ left: pct((s.x1 + s.x2) / 2 - view.x, view.w), top: pct((s.y1 + s.y2) / 2 - view.y, view.h) }}
          >
            {l.label}
          </span>
        )
      })}

      {boxes.map((base) => {
        const box = boxOf(base.id) ?? base
        const b = byId.get(base.id)!
        const selected = b.id === selectedId
        const badge = badges?.[b.id] ?? 0
        return (
          <div
            key={b.id}
            className='group absolute'
            style={{
              left: pct(box.x - view.x, view.w),
              top: pct(box.y - view.y, view.h),
              width: pct(box.w, view.w),
              height: pct(box.h, view.h),
            }}
          >
            <button
              type='button'
              data-building={b.id}
              onPointerDown={(e) => begin(e, b.id, 'press')}
              // The read-only map, and the keyboard (Enter) on the editable one.
              onClick={(e) => {
                if (!editable || e.detail === 0) onSelect(b.id)
              }}
              aria-pressed={selected}
              aria-label={badge > 0 ? `${b.name}, ${badge} ${badge === 1 ? 'room needs' : 'rooms need'} attention` : b.name}
              title={b.description ? `${b.name} — ${b.description}` : b.name}
              className={cn(
                'relative flex h-full w-full flex-col overflow-hidden rounded-[3px] border-2 text-left transition-colors',
                editable && (live?.id === b.id ? 'cursor-grabbing' : 'cursor-grab'),
                hovered === b.id
                  ? 'border-[#1a73e8] bg-[#e8f0fe] ring-2 ring-[#1a73e8]/40'
                  : selected
                    ? // A detailed tile, or one showing its schematic, stays readable: picked
                      // shows as a blue outline. The simple box (Overall View) fills blue, as
                      // the spec draws it.
                      renderBuilding || b.schematicUrl
                      ? 'border-[#1a73e8] bg-white text-[#1e2a45] shadow-md ring-2 ring-[#1a73e8]/45'
                      : 'border-[#1a73e8] bg-[#1a73e8] text-white shadow-md'
                    : 'border-[#2b2f36] bg-white text-[#1e2a45] hover:bg-gray-50',
              )}
            >
              {b.schematicUrl && (
                // The building drawn to its schematic: the whole plan, uncropped.
                <img
                  src={b.schematicUrl}
                  alt={`${b.name} schematic`}
                  draggable={false}
                  className='pointer-events-none absolute inset-0 h-full w-full object-contain p-0.5'
                />
              )}
              {renderBuilding ? (
                renderBuilding(b, selected)
              ) : b.schematicUrl ? (
                <span
                  className={cn(
                    'relative mt-auto flex w-full items-center justify-center gap-1 px-1 py-0.5 text-center text-[12px] font-bold leading-tight',
                    selected ? 'bg-[#1a73e8] text-white' : 'bg-white/90',
                  )}
                >
                  {selected && <MapPin className='h-3.5 w-3.5 shrink-0' strokeWidth={2} />}
                  <span className='truncate'>{b.name}</span>
                </span>
              ) : (
                <span className='relative m-auto flex flex-col items-center gap-1 px-1 text-center text-[14px] font-bold leading-tight'>
                  {b.name}
                  {selected && <MapPin className='h-4 w-4' strokeWidth={2} />}
                </span>
              )}
            </button>
            {editable && (
              <>
                {/* Drag onto another building to connect them. */}
                <span
                  onPointerDown={(e) => begin(e, b.id, 'connect')}
                  className='absolute -right-2 top-1/2 h-4 w-4 -translate-y-1/2 cursor-crosshair rounded-full border-2 border-white bg-[#1a73e8] opacity-0 shadow transition-opacity group-hover:opacity-100'
                  role='presentation'
                  data-connect={b.id}
                  title='Drag onto another building to connect them'
                />
                {/* Drag to resize. */}
                <span
                  onPointerDown={(e) => begin(e, b.id, 'resize')}
                  className='absolute -bottom-1 -right-1 h-3 w-3 cursor-nwse-resize rounded-sm border-2 border-white bg-[#5f6b7a] opacity-0 shadow transition-opacity group-hover:opacity-100'
                  role='presentation'
                  data-resize={b.id}
                  title='Drag to resize'
                />
              </>
            )}
            {badge > 0 && !editable && (
              <span
                aria-hidden
                className='pointer-events-none absolute -right-2.5 -top-2.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#e5221f] text-[11px] font-bold text-white shadow'
              >
                {badge}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )

  if (!scroll) return map
  return (
    <div ref={scroller} className='max-h-[460px] overflow-auto rounded-md bg-[#eef0f2]'>
      <div className='mx-auto w-max'>{map}</div>
    </div>
  )
}
